import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getFirebaseEmulatorConfig } from "./firebaseEmulatorConfig";

export type RuntimeEnvironment =
  | "DEVELOPMENT"
  | "TESTING"
  | "STAGING"
  | "PRODUCTION"
  | "CANARY";

const initializeDefaultApp = (config: FirebaseOptions): FirebaseApp => {
  if (getApps().length > 0) {
    return getApp();
  }

  return initializeApp(config);
};

const fetchFirebaseInitConfig = async (): Promise<FirebaseOptions> => {
  const response = await fetch("/__/firebase/init.json");

  if (!response.ok) {
    throw new Error(
      `Failed to load Firebase init config from /__/firebase/init.json (status ${response.status})`,
    );
  }

  return (await response.json()) as FirebaseOptions;
};

const fetchLocalDevFirebaseConfig = async (): Promise<FirebaseOptions> => {
  const response = await fetch("/firebase.dev.json");

  if (!response.ok) {
    throw new Error(
      `Failed to load local dev Firebase config from /firebase.dev.json (status ${response.status})`,
    );
  }

  return (await response.json()) as FirebaseOptions;
};

const mapProjectIdToEnvironment = (projectId: string): RuntimeEnvironment => {
  if (projectId === "job-manager-staging") {
    return "STAGING";
  }

  if (projectId === "job-manager-1313") {
    return "PRODUCTION";
  }

  throw new Error(
    `Unknown Firebase projectId '${projectId}' from /__/firebase/init.json`,
  );
};

const resolveDevLikeEnvironment = (): RuntimeEnvironment => {
  return import.meta.env.MODE === "test" ? "TESTING" : "DEVELOPMENT";
};

const getAuthDomainOverride = (
  environment: RuntimeEnvironment,
): string | null => {
  if (environment === "STAGING") {
    return "auth.staging.jobmgr.app";
  }

  if (environment === "PRODUCTION") {
    return "auth.jobmgr.app";
  }

  return null;
};

const createFirebaseBootstrap = async (): Promise<{
  app: FirebaseApp;
  environment: RuntimeEnvironment;
}> => {
  let app: FirebaseApp;
  const isProdLike = import.meta.env.PROD;
  const devLikeEnvironment = resolveDevLikeEnvironment();

  if (!isProdLike && import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true") {
    const firebaseEmulator = getFirebaseEmulatorConfig();

    app = initializeDefaultApp({
      projectId: "demo-job-manager",
      apiKey: "dummy",
      authDomain: `${firebaseEmulator.host}:${firebaseEmulator.authPort}`,
      storageBucket: "demo-job-manager.appspot.com",
    });

    const auth = getAuth(app);

    connectAuthEmulator(
      auth,
      `https://${firebaseEmulator.host}:${firebaseEmulator.authPort}`,
      {
        disableWarnings: true,
      },
    );

    const storage = getStorage(app);
    connectStorageEmulator(
      storage,
      firebaseEmulator.host,
      firebaseEmulator.storagePort,
    );
    /** @ts-expect-error We are hacking ssl support */
    storage["_protocol"] = "https";

    const functions = getFunctions(app);
    connectFunctionsEmulator(
      functions,
      firebaseEmulator.host,
      firebaseEmulator.functionsPort,
    );

    const db = getFirestore();
    connectFirestoreEmulator(
      db,
      firebaseEmulator.host,
      firebaseEmulator.firestorePort,
    );

    /** @ts-expect-error We are hacking ssl support */
    db["_settings"]["ssl"] = true;

    return {
      app,
      environment: devLikeEnvironment,
    };
  }

  let firebaseConfig: FirebaseOptions;
  let environment: RuntimeEnvironment;

  if (isProdLike) {
    firebaseConfig = await fetchFirebaseInitConfig();
    if (!firebaseConfig.projectId) {
      throw new Error("Firebase init config is missing projectId.");
    }
    environment = mapProjectIdToEnvironment(firebaseConfig.projectId);

    const authDomainOverride = getAuthDomainOverride(environment);
    if (authDomainOverride) {
      firebaseConfig = {
        ...firebaseConfig,
        authDomain: authDomainOverride,
      };
    }
  } else {
    firebaseConfig = await fetchLocalDevFirebaseConfig();
    environment = devLikeEnvironment;
  }

  return {
    app: initializeDefaultApp(firebaseConfig),
    environment,
  };
};

// Note we no longer export firestore, auth etc
// As this was affecting bundle size.
const firebaseBootstrap = await createFirebaseBootstrap();

export const app = firebaseBootstrap.app;
export const JM_ENVIRONMENT = firebaseBootstrap.environment;
